import React, { useEffect, useRef, useState } from 'react'
import { Mail, OctagonAlert, Loader2, CheckCircle2 } from 'lucide-react'
import { cn } from 'lib/utils'
import { useTranslation } from 'react-i18next'
import { emailConfigApi } from 'lib/api'
import { useEmailSending } from 'contexts/EmailSendingContext'

interface EmailSendingIndicatorProps {
  className?: string
}

export function EmailSendingIndicator({ className }: EmailSendingIndicatorProps) {
  const { t } = useTranslation()
  const { emailSending, updateEmailTask, removeEmailTask, hasActiveTasks } = useEmailSending()
  const [isOpen, setIsOpen] = useState(false)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  // Get the first active task (or most recent)
  const activeTasks = Array.from(emailSending.activeTasks.values())
    .filter(task => task.status === 'processing' || task.status === 'pending')
    .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())

  const currentTask = activeTasks[0] || null

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Poll for task status of all active tasks
  useEffect(() => {
    if (activeTasks.length === 0) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
      return
    }

    const pollTaskStatuses = async () => {
      for (const task of activeTasks) {
        try {
          const response = await emailConfigApi.getBulkEmailTaskStatus(task.taskId)
          const data = response.data

          if (data.status === 'completed' && data.result) {
            const result = data.result
            updateEmailTask(task.taskId, {
              status: 'completed',
              success: result.success,
              failed: result.failed,
              errors: result.errors || [],
              completedAt: new Date().toISOString()
            })
            
            // Backend sends summary notification via SSE when bulk task completes
            
            // Auto-remove completed tasks after 10 seconds
            setTimeout(() => {
              removeEmailTask(task.taskId)
            }, 10000)
          } else if (data.status === 'failed') {
            updateEmailTask(task.taskId, {
              status: 'failed',
              errors: [data.error || 'Task failed'],
              completedAt: new Date().toISOString()
            })
            
            // Backend sends failure notification via SSE when bulk task fails
            
            // Auto-remove failed tasks after 15 seconds
            setTimeout(() => {
              removeEmailTask(task.taskId)
            }, 15000)
          } else if (data.result) {
            // Update progress while processing
            updateEmailTask(task.taskId, {
              success: data.result.success || 0,
              failed: data.result.failed || 0,
              errors: data.result.errors || []
            })
          }
        } catch (error: any) {
          console.error(`Error polling task ${task.taskId} status:`, error)
        }
      }
    }

    // Poll immediately, then every 3 seconds
    pollTaskStatuses()
    pollingIntervalRef.current = setInterval(pollTaskStatuses, 3000)

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [activeTasks, updateEmailTask, removeEmailTask])

  // Auto-close popover after 5 seconds when all tasks completed
  useEffect(() => {
    if (!hasActiveTasks && activeTasks.length === 0) {
      const timer = setTimeout(() => {
        setIsOpen(false)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [hasActiveTasks, activeTasks])

  // Set selected task when opened
  useEffect(() => {
    if (isOpen && !selectedTaskId && currentTask) {
      setSelectedTaskId(currentTask.taskId)
    }
  }, [isOpen, selectedTaskId, currentTask])

  // Don't show indicator if no active tasks
  if (!hasActiveTasks || !currentTask) {
    return null
  }

  const displayTask = selectedTaskId 
    ? emailSending.activeTasks.get(selectedTaskId) || currentTask
    : currentTask

  const progress = displayTask.total > 0
    ? Math.round(((displayTask.success + displayTask.failed) / displayTask.total) * 100)
    : 0

  const status = displayTask.status
  const error = displayTask.errors.length > 0 ? displayTask.errors[0] : null

  const panelContent = (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border px-4 py-3',
        error || status === 'failed'
          ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-100'
          : status === 'completed'
          ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/30 dark:text-green-100'
          : 'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-100'
      )}
    >
      {error || status === 'failed' ? (
        <OctagonAlert className="mt-0.5 h-5 w-5 flex-shrink-0" />
      ) : status === 'completed' ? (
        <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0" />
      ) : (
        <Mail className="mt-0.5 h-5 w-5 flex-shrink-0 animate-pulse" />
      )}
      <div className="flex-1 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">
            {status === 'completed'
              ? t('kanban.emailSending.emailsSent')
              : status === 'failed'
              ? t('kanban.emailSending.emailSendingFailed')
              : t('kanban.emailSending.sendingEmails')}
          </p>
          {activeTasks.length > 1 && (
            <span className="text-xs opacity-70">{activeTasks.length} active</span>
          )}
        </div>

        {displayTask ? (
          <div className="space-y-2">
            {/* Progress Bar */}
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span>{t('kanban.emailSending.progress')}</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className={cn(
                    'h-2 rounded-full transition-all duration-300',
                    status === 'completed' && displayTask.failed === 0
                      ? 'bg-green-500'
                      : status === 'completed'
                      ? 'bg-yellow-500'
                      : 'bg-blue-500'
                  )}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="opacity-80">{t('kanban.emailSending.total')}</span>
                <span className="font-semibold">{displayTask.total}</span>
              </div>
              {displayTask.success > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="opacity-80">{t('kanban.emailSending.sent')}</span>
                  <span className="font-semibold text-green-600 dark:text-green-400">{displayTask.success}</span>
                </div>
              )}
              {displayTask.failed > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="opacity-80">{t('kanban.emailSending.failed')}</span>
                  <span className="font-semibold text-red-600 dark:text-red-400">{displayTask.failed}</span>
                </div>
              )}
            </div>

            {/* Task Selector (if multiple tasks) */}
            {activeTasks.length > 1 && (
              <div className="mt-2 pt-2 border-t border-gray-300 dark:border-gray-600">
                <p className="text-xs font-semibold mb-1">{t('kanban.emailSending.activeTasks')}</p>
                <div className="space-y-1 max-h-24 overflow-y-auto">
                  {activeTasks.map(task => (
                    <button
                      key={task.taskId}
                      onClick={() => setSelectedTaskId(task.taskId)}
                      className={cn(
                        'w-full text-left text-xs p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800',
                        selectedTaskId === task.taskId && 'bg-blue-100 dark:bg-blue-900/30'
                      )}
                    >
                      Task: {task.total} emails - {task.success + task.failed}/{task.total}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Errors */}
            {displayTask.errors && displayTask.errors.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-300 dark:border-gray-600">
                <p className="text-xs font-semibold mb-1">{t('kanban.emailSending.errors')}</p>
                <ul className="text-xs space-y-1 max-h-24 overflow-y-auto">
                  {displayTask.errors.slice(0, 3).map((err, idx) => (
                    <li key={idx} className="opacity-90">• {err}</li>
                  ))}
                  {displayTask.errors.length > 3 && (
                    <li className="opacity-70">... and {displayTask.errors.length - 3} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        ) : status === 'processing' ? (
          <p className="text-xs opacity-80">{t('kanban.emailSending.processingMessage')}</p>
        ) : error ? (
          <p className="text-xs">{error}</p>
        ) : null}
      </div>
    </div>
  )

  return (
    <>
      <div className={cn('relative flex items-center', className)}>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className={cn(
            'relative flex h-9 w-9 items-center justify-center rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
            error || status === 'failed'
              ? 'border-red-400 text-red-600 bg-red-50 dark:bg-red-900/40 dark:border-red-800'
              : status === 'completed'
              ? 'border-green-400 text-green-600 bg-green-50 dark:bg-green-900/40 dark:border-green-800'
              : 'border-blue-400 text-blue-600 bg-blue-50 dark:bg-blue-900/40 dark:border-blue-700'
          )}
          title={
            status === 'completed'
              ? t('kanban.emailSending.emailsSentSuccessfullyTooltip')
              : status === 'failed'
              ? t('kanban.emailSending.emailSendingFailedTooltip')
              : t('kanban.emailSending.sendingInBackground')
          }
          aria-expanded={isOpen}
        >
          {error || status === 'failed' ? (
            <OctagonAlert className="h-4 w-4" />
          ) : status === 'completed' ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <Mail className="h-4 w-4 animate-pulse" />
          )}
          {displayTask && displayTask.total > 0 && (
            <span
              className={cn(
                'absolute -top-1 -right-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white',
                  status === 'completed' && displayTask.failed === 0
                  ? 'bg-green-500'
                  : status === 'completed'
                  ? 'bg-yellow-500'
                  : 'bg-blue-600'
              )}
            >
              {displayTask.total}
            </span>
          )}
          {activeTasks.length > 1 && (
            <span className="absolute -bottom-1 -right-1 inline-flex h-3 w-3 items-center justify-center rounded-full bg-blue-600 text-[8px] font-semibold text-white">
              {activeTasks.length}
            </span>
          )}
        </button>

        {isOpen && (
          <div
            ref={popoverRef}
            className="absolute right-0 top-12 z-50 w-[400px] max-w-md rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl"
          >
            <div className="px-6 py-4 text-sm">
              {panelContent}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

